// Generic entity attachments API — Drive Phase 2 FE cutover.
//
// Real engine (prod, USE_REAL_API) talks to the native generic surface:
//   list   → GET  /drive/attachments/{component}/{object_id}   (presigned URLs)
//   upload → POST .../upload-url → browser PUT (same-origin) → POST .../confirm
//   delete → DELETE /drive/files/{id}
// The endpoints auto-ensure the owning-org folder server-side (resolved from the
// entity, not the actor) — admin Ring 3 reaches a seller's folder; a seller is
// pinned to their own. Maket mode (dev, MSW) uses a metadata-only stub on the
// same path prefix. The gate is the codebase-wide USE_REAL_API toggle.

import { useQuery } from '@tanstack/react-query'
import { USE_REAL_API, apiUrl } from '../lib/api'

export type AttachmentRow = {
  id: string
  role: string
  original_filename: string
  content_type: string
  content_type_verified?: string | null
  file_size: number
  thumbnail_url: string | null
  download_url: string | null
  uploaded_at: string
  uploaded_by_user_id: string | null
  // Phase 3 lifecycle (present on the real DriveFileRead; optional for older sims).
  file_purpose?: string | null
  is_locked?: boolean
  approved_at?: string | null
}

export type MessagingAttachmentComponent = 'messaging_conversation'

export class AttachmentRequestError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'AttachmentRequestError'
    this.status = status
  }
}

export type AttachmentUploadStage = 'request' | 'storage' | 'confirm'

export class AttachmentUploadError extends Error {
  readonly stage: AttachmentUploadStage
  readonly status?: number
  readonly code?: string

  constructor(
    message: string,
    stage: AttachmentUploadStage,
    status?: number,
    code?: string,
  ) {
    super(message)
    this.name = 'AttachmentUploadError'
    this.stage = stage
    this.status = status
    this.code = code
  }
}

async function attachmentUploadError(
  response: Response,
  stage: AttachmentUploadStage,
  fallback: string,
): Promise<AttachmentUploadError> {
  const problem = (await response.json().catch(() => null)) as
    | {
        detail?: string | { code?: unknown; message?: unknown }
        title?: unknown
      }
    | null
  const detail = problem?.detail
  const message =
    typeof detail === 'string'
      ? detail
      : detail && typeof detail.message === 'string'
        ? detail.message
        : typeof problem?.title === 'string'
          ? problem.title
          : fallback
  const code =
    detail && typeof detail !== 'string' && typeof detail.code === 'string'
      ? detail.code
      : undefined

  return new AttachmentUploadError(message, stage, response.status, code)
}

export function messagingAttachmentComponent(): MessagingAttachmentComponent {
  return 'messaging_conversation'
}

export function messagingAttachmentQueryKey(conversationId: string) {
  return [
    'drive-attachments',
    messagingAttachmentComponent(),
    conversationId,
  ] as const
}

export function messagingAttachmentQueryRoot() {
  return ['drive-attachments', messagingAttachmentComponent()] as const
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export type MessagingAttachmentSource = {
  component: MessagingAttachmentComponent
  objectId: string
  rows: AttachmentRow[]
  rowsById: Map<string, AttachmentRow>
  isLoading: boolean
  isError: boolean
  refetch: () => Promise<unknown>
}

export function useMessagingAttachmentSource(
  conversationId: string,
): MessagingAttachmentSource {
  const component = messagingAttachmentComponent()
  const query = useQuery({
    queryKey: messagingAttachmentQueryKey(conversationId),
    queryFn: () => fetchAttachments(component, conversationId),
    retry: false,
    refetchOnWindowFocus: false,
  })
  const rows = query.data ?? []

  return {
    component,
    objectId: conversationId,
    rows,
    rowsById: new Map(rows.map((row) => [row.id, row])),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

function attachmentRow(value: unknown): AttachmentRow {
  if (
    typeof value !== 'object' ||
    value === null ||
    typeof (value as { id?: unknown }).id !== 'string' ||
    typeof (value as { original_filename?: unknown }).original_filename !==
      'string' ||
    typeof (value as { content_type?: unknown }).content_type !== 'string' ||
    typeof (value as { file_size?: unknown }).file_size !== 'number'
  ) {
    throw new Error('Drive upload returned an invalid attachment row')
  }
  if (
    Object.prototype.hasOwnProperty.call(value, 'content_type_verified') &&
    (value as { content_type_verified: unknown }).content_type_verified !== null &&
    typeof (value as { content_type_verified: unknown }).content_type_verified !==
      'string'
  ) {
    throw new Error('Drive upload returned an invalid attachment row')
  }
  return value as AttachmentRow
}

function attBase(component: string, objectId: string): string {
  return `/drive/attachments/${component}/${encodeURIComponent(objectId)}`
}

export async function fetchAttachments(
  component: string,
  objectId: string,
): Promise<AttachmentRow[]> {
  const res = await fetch(apiUrl(attBase(component, objectId)))
  if (!res.ok) {
    throw new AttachmentRequestError(
      `fetch attachments failed: ${res.status}`,
      res.status,
    )
  }
  const data = (await res.json()) as { items: AttachmentRow[] }
  return data.items
}

async function uploadReal(
  component: string,
  objectId: string,
  file: File,
  role: string,
): Promise<AttachmentRow> {
  const contentType = file.type || 'application/octet-stream'
  const urlRes = await fetch(apiUrl(`${attBase(component, objectId)}/upload-url`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      original_filename: file.name,
      content_type: contentType,
      file_size: file.size,
    }),
  })
  if (!urlRes.ok) {
    throw await attachmentUploadError(
      urlRes,
      'request',
      `upload-url failed: ${urlRes.status}`,
    )
  }
  const { upload_url, provider_key } = (await urlRes.json()) as {
    upload_url: string
    provider_key: string
  }

  // Browser → object storage direct (same-origin). The presigned signature
  // covers Content-Type — it MUST match the upload-url value.
  const putRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  })
  if (!putRes.ok) {
    throw await attachmentUploadError(
      putRes,
      'storage',
      `storage upload failed: ${putRes.status}`,
    )
  }

  const confRes = await fetch(apiUrl(`${attBase(component, objectId)}/confirm`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      provider_key,
      original_filename: file.name,
      role,
    }),
  })
  if (!confRes.ok) {
    throw await attachmentUploadError(
      confRes,
      'confirm',
      `confirm failed: ${confRes.status}`,
    )
  }
  return attachmentRow(await confRes.json())
}

export async function uploadAttachment(
  component: string,
  objectId: string,
  file: File,
  role = 'document',
): Promise<AttachmentRow> {
  if (USE_REAL_API) {
    return uploadReal(component, objectId, file, role)
  }
  // Maket: metadata-only stub (no real bytes).
  const res = await fetch(apiUrl(attBase(component, objectId)), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      original_filename: file.name,
      content_type: file.type || 'application/octet-stream',
      file_size: file.size,
      role,
    }),
  })
  if (!res.ok) {
    throw await attachmentUploadError(
      res,
      'request',
      `upload failed: ${res.status}`,
    )
  }
  return attachmentRow(await res.json())
}

export async function deleteAttachment(
  component: string,
  objectId: string,
  fileId: string,
): Promise<void> {
  const path = USE_REAL_API
    ? `/drive/files/${fileId}`
    : `${attBase(component, objectId)}/${fileId}`
  const res = await fetch(apiUrl(path), { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw new Error(`delete failed: ${res.status}`)
}
